import {
  FilterFieldConfigBase,
  FilterPreset,
  KeysMatching,
} from '@warebee/shared/common';
import {
  SortDirection,
  StringSearchFilter,
  StringSearchFilterType,
} from '@warebee/shared/data-access-api-dto';
import { InferResult, sql } from 'kysely';
import _ from 'lodash';
import { ActualityQueryBuilderParams } from '../datasetQueryBuilder.types';
import { getItemsQueryBuilder } from '../itemSet/itemsQueryBuilder';
import { getLocationQueryBuilder } from '../layoutLocations/locationsQueryBuilder';
import { orderStatsDataColumnsAll } from '../orderSet/orderSetStatsQueryBuilder';
import { feedExtendedEventsQueryBuilder } from '../queryBuilder/feedExtendedEventsQueryBuilder';
import { getAssignmentQueryBuilder } from './assignmentsQueryBuilder';

const assignmentHqAllFieldsStub = (params: AssignmentHqQueryBuilderParams) =>
  getAssignmentHqQueryBuilder(params).selectFrom('hq').selectAll();

export type AssignmentHqRows = InferResult<
  ReturnType<typeof assignmentHqAllFieldsStub>
>;

export type AssignmentHqRow = AssignmentHqRows[number];
export type AssignmentHqField = keyof AssignmentHqRow;

export type AssignmentHqQueryBuilderParams = ActualityQueryBuilderParams & {
  filterBy?: AssignmentHqFilterBy;
  filterPreset?: FilterPreset<AssignmentHqField>;
  filterConfig: FilterFieldConfigBase<AssignmentHqField>[];
};

export type AssignmentHqSortBy = {
  direction?: SortDirection;
  field: AssignmentHqFieldWithStringFilter;
};

export type AssignmentHqFieldWithStringFilter = KeysMatching<
  AssignmentHqRow,
  string
>;

export type AssignmentHqFilterBy = Partial<Record<string, StringSearchFilter>>;

export function getAssignmentHqQueryBuilder(
  params: AssignmentHqQueryBuilderParams,
) {
  let builder = feedExtendedEventsQueryBuilder(params)
    .with(
      cte => cte('is'),
      db => {
        return getItemsQueryBuilder(params.itemSet, db);
      },
    )
    .with(
      cte => cte('l'),
      db => {
        return getLocationQueryBuilder(params.layout, db);
      },
    )
    .with(
      cte => cte('a'),
      db => {
        return getAssignmentQueryBuilder(params.assignment, db);
      },
    )
    .with(
      cte => cte('hqRaw'),
      db => {
        return db
          .selectFrom('a')
          .leftJoin('is', join =>
            join
              .onRef('a.consignee', '=', 'is.consignee')
              .onRef('a.sku', '=', 'is.sku')
              .onRef('a.stockUom', '=', 'is.uom'),
          )
          .fullJoin('l', join =>
            join.onRef('a.locationId', '=', 'l.locationId'),
          )
          .leftJoin('item_stats', join =>
            join.onRef('a.skuKey', '=', 'item_stats.skuKey'),
          )
          .selectAll('a')
          .select(orderStatsDataColumnsAll)
          .select([
            'is.skuGroup',
            'is.name',
            'is.description',
            'is.subGroup',
            'is.transportClass',
            'is.stockCategory',
            'is.storageClass',
            'is.pollutionClass',
            'is.lowerUom',
            'is.netWeight',
            'is.length',
            'is.width',
            'is.height',
            'is.volume',
            'is.unitsPerLowestUom',
            'is.ean',
            'is.upc',
            'l.locationId as layoutLocationId',
            'l.locationOrder',
            'l.locationStatus',
            'l.locationLevel',
            'l.locationLength',
            'l.locationWidth',
            'l.locationHeight',
            'l.locationWeight',
            'l.locationBayId',
            'l.locationBayTitle',
            'l.locationBayPosition',
            'l.locationDepthPosition',
            'l.locationUsageType',
            'l.locmhtype',
            'l.locationRackingType',
            'l.warehouseArea',
            'l.locationSide',
            'l.congestionZone',
            'l.locationBayProjection',
            'l.locationHeightFromFloor',
            'l.locationDepthFromFront',
            'l.locationIndexFromFront',
            'l.bayType',
            'l.aisleId',
            'l.aisleTitle',
            'l.planeId',
            'l.planeTitle',
          ])
          .select(
            sql<number>`${sql.ref('is.volume')} * ${sql.ref('a.stockUomQuantity')} / ${sql.ref('l.locationVolume')}`.as(
              'locationStockOccupancyRate',
            ),
          );
      },
    )
    .with(
      cte => cte('hq'),
      db => {
        let builder = db.selectFrom('hqRaw').selectAll();
        if (params.filterBy) {
          builder = _.reduce(
            params.filterBy,
            (q, value, key) => {
              if (
                value?.type === StringSearchFilterType.CONTAINS &&
                value?.value === 'undefined'
              ) {
                return q.where(key as any, 'is', null);
              }

              if (
                value?.type === StringSearchFilterType.CONTAINS &&
                !_.isEmpty(value?.value)
              ) {
                return q.where(key as any, 'like', `%${value.value}%`);
              }

              return q;
            },
            builder,
          );
        }
        return builder;
      },
    );

  return builder;
}
